import { Injectable } from '@angular/core';
import { isBefore, parseISO, set } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class DateUtilityLib {
  /**
   * Set a time on a given date. The seconds and milliseconds will always be set to zero.
   *
   * @param date The date to modify
   * @param hours Hours to set, default 0
   * @param minutes Minutes to set, default 0
   * @returns
   */
  static setTime(date: Date | string, hours = 0, minutes = 0) {
    const sourceDate = DateUtilityLib.ensureDate(date);
    return set(sourceDate, { hours, minutes, seconds: 0, milliseconds: 0 });
  }

  /**
   * Return true if date1 is before date2.
   *
   * @param date1
   * @param date2
   * @returns
   */
  static isBefore(date1: Date | string, date2: Date | string) {
    const sourceDate1 = DateUtilityLib.ensureDate(date1);
    const sourceDate2 = DateUtilityLib.ensureDate(date2);
    return isBefore(sourceDate1, sourceDate2);
  }

  private static ensureDate(maybeDate: Date | string): Date {
    if (typeof maybeDate === 'string') {
      return parseISO(maybeDate);
    }
    return maybeDate;
  }

  /**
   * Add days to the date
   * @param date
   * @param days
   * @returns
   */
  static addDaysToDate(date: string | Date, days: number): Date {
    const addedDays = typeof date === 'string' ? new Date(date) : date;
    addedDays.setDate(addedDays.getDate() + days);
    return addedDays;
  }

  /**
   *  This method is used to calculate Total Working Days between 2 days excluding weekends
   *
   * @param {startDate} Date
   * @param {endDate} Date
   * @param {workingHrs} Number
   * @param {workStartHour} Number
   *
   */
  calculateTotalWorkingDays(startDate: Date, endDate: Date, workingHrs = 8, workStartHour = 9) {
    let firstDayFTE = 0;
    let lastDayFTE = 0;
    let totalWorkingDays = 0;
    const officeFinishHour = workStartHour + workingHrs;

    //calculating the hrs of first day
    const startHour = startDate.getHours();
    if (startHour >= workStartHour) {
      let firstDayHrs = officeFinishHour - startHour;
      if (firstDayHrs < 0) firstDayHrs = 0;
      firstDayFTE = firstDayHrs / workingHrs;
      startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1, 0, 0, 0);
    }

    //calculating the hrs of last day
    const finishHour = endDate.getHours();
    if (finishHour <= officeFinishHour) {
      let lastDayHrs = finishHour - workStartHour;
      if (lastDayHrs < 0) lastDayHrs = 0;
      lastDayFTE = lastDayHrs / workingHrs;
      endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 1, 0, 0, 0);
    }

    //getting the days between first and last
    const days = this.getDatesInRange(startDate, endDate);
    totalWorkingDays = firstDayFTE + days.length + lastDayFTE;

    const obj = {
      firstDayFTE: firstDayFTE,
      lastDayFTE: lastDayFTE,
      betweenDays: days.length,
      totalWorkingDays: totalWorkingDays,
    };
    return obj;
  }
  /**
   *  This method is used excluding weekends
   *
   * @param {startDate} Date
   * @param {endDate} Date
   *
   */
  getDatesInRange(startDate: Date, endDate: Date): Date[] {
    const date = new Date(startDate.getTime());
    const dates = [];

    while (date <= endDate) {
      const dateToPush = new Date(date);

      if (dateToPush.getDay() != 0 && dateToPush.getDay() != 6) {
        dates.push(dateToPush);
      }
      date.setDate(date.getDate() + 1);
    }

    return dates;
  }

  getDifferenceInDays(date1: Date, date2: Date) {
    const diffTime = date2.getTime() - date1.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays;
  }

  /**
   * Note: We can also use getDayNameShort() along with lowerCase() instead
   */
  getDayNameFromDayNum(day: number) {
    switch (day) {
      case 0:
        return 'sun';
      case 1:
        return 'mon';
      case 2:
        return 'tue';
      case 3:
        return 'wed';
      case 4:
        return 'thu';
      case 5:
        return 'fri';
      case 6:
        return 'sat';
      default:
        return '';
    }
  }

  getMonthNameFromMonthNum(month: number) {
    switch (month) {
      case 0:
        return 'jan';
      case 1:
        return 'feb';
      case 2:
        return 'mar';
      case 3:
        return 'apr';
      case 4:
        return 'may';
      case 5:
        return 'jun';
      case 6:
        return 'jul';
      case 7:
        return 'aug';
      case 8:
        return 'sep';
      case 9:
        return 'oct';
      case 10:
        return 'nov';
      case 11:
        return 'dec';
      default:
        return '';
    }
  }

  /** Returns the number of week by date. Total 52 week in one year */
  calculateWeekNum(currentDate: Date): number {
    const startDate = new Date(currentDate.getFullYear(), 0, 1);
    const days = Math.floor((currentDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));

    const weekNumber = Math.ceil((currentDate.getDay() + 1 + days) / 7);
    return weekNumber;
  }

  /**
   * @param date
   */
  isLeapYear(date: Date) {
    const year = date.getFullYear();
    if ((year & 3) != 0) return false;
    return year % 100 != 0 || year % 400 == 0;
  }

  /**
   * @see getDayOfYear
   * Note: use getDayOfYear instead
   */
  getDOYFromDate(date: Date): number {
    const dayCount = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const mn = date.getMonth();
    const dn = date.getDate();
    let dayOfYear = dayCount[mn] + dn; // 0-365
    if (mn > 1 && this.isLeapYear(date)) dayOfYear++; // add 1 for leap years
    return dayOfYear;
  }

  getCurrentWeekMonday(d: Date) {
    const day = d.getDay();
    const diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
  }

  /**
   * Function to get day of the year from date.
   */
  getDayOfYear(date: Date) {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const day = Math.floor(diff / oneDay);
    return day;
  }
}

/**
 * Make the given date a UTC without changing its value.
 * This is useful if, for example we want to store only the date (no time) and timezone should not matter.
 * E.g. the deadline of a project is the 26.05.2023 no matter from where we edit the project.
 *
 * @param date
 * @returns
 */
export function dateAsUTC(date?: Date | string): string | undefined {
  let utc;
  if (typeof date === 'string' && date !== '') {
    date = new Date(date);
  }

  if (date instanceof Date) {
    utc = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  }
  return utc;
}
